import { db, DEXIE_STORES } from '@/database'
import { ProfileModelFactory } from '@/models/Profile.model'
import { myCompaniesStore } from '@/store/companies/my_companies.store'
import { profilesService } from '@/store/profiles/profiles.service'
import { Profile, ProfileRole } from '@roolz/types/api/profiles'
import { Company } from '@roolz/types/api/companies'
import { liveQuery } from 'dexie'
import { merge, mergeWith, keyBy } from 'lodash'
import { makeAutoObservable } from 'mobx'
import { persist, retrieveClientside } from '@roolz/sdk/utils/auth'
import { ProfileModel } from '@/types/models/profile'
import { RecursivePartial } from '@roolz/types'
import { Device } from '@roolz/types/api/profiles'
import { Offer } from '@roolz/types/api/exchange'

class ProfilesStore {
  my_profile?: ProfileModel
  my_device?: Device

  profiles: Record<Profile["id"], ProfileModel> = {}
  presenceSubscriptionQueue: Array<Profile['id']> = []

  constructor() {
    makeAutoObservable(this)
  }

  get activeCompanyId(): Company['id'] | null {
    const company = myCompaniesStore.companies.find(({ id }) => id === this.my_profile?.active_space_company_id)

    return company?.id ?? null
  }

  get bannedProfiles() {
    return Object.values(this.profiles).filter(profile => profile.relationships?.is_banned)
  }

  /**
   * If required fields of profile filled, or show the Profile Fill page to fill them
   */
  get isMyProfileFilled(): boolean {
    return !!this.my_profile?.is_filled
  }

  addOrUpdateProfile = (profile: Profile) => {
    if(this.updateProfileIfExists(profile)) {
      return
    }

    this.presenceSubscriptionQueue.push(profile.id)
    profilesService.bindToProfilesOnlineChanges()
    liveQuery(() => db[DEXIE_STORES.PROFILES].get(profile.id))
      .subscribe(profile => {
        profile && this.updateProfileIfExists(profile)
      })

    const model = ProfileModelFactory(profile)
    this.profiles[model.id] = model

    return model
  }

  updateProfileIfExists = (profile: Profile) => {
    const existing = this.profiles[profile.id]

    if(existing) {
      mergeWith(existing, profile, (a: unknown, b: unknown) => {
        if(Array.isArray(b)) return b
      })

      return true
    }

    return false
  }

  setMyProfile(profile: Profile) {
    retrieveClientside().then((credentials: any) => {
      if(credentials && profile.lang_code && credentials?.lang_code !== profile.lang_code) {
        persist({ ...credentials, lang_code: profile.lang_code })
      }
    }).catch(console.log)

    this.addOrUpdateProfile(profile)

    this.my_profile = ProfileModelFactory(profile)
  }

  setMyDevice(device: Device) {
    this.my_device = device
  }

  findProfile = (id: Profile['id']): ProfileModel | undefined => {
    if(id == this.my_profile?.id) {
      return this.my_profile
    }

    return this.profiles[id]
  }

  patchProfile(id: Profile['id'], data: RecursivePartial<Profile>) {
    const profile = this.findProfile(id)

    if(profile) {
      merge(profile, data)
    }
  }

  hasAccessToOffer(offer: Offer): boolean {
    if([
      ProfileRole.Admin,
      ProfileRole.Moderator
    ].includes(this.my_profile?.role)) {
      return true
    }

    if(!offer.company_id) {
      return this.activeCompanyId ? false : this.my_profile?.id === offer.creation_profile_id
    } else {
      return this.activeCompanyId === offer.company_id
    }
  }

  canBanProfile(profile: ProfileModel): boolean {
    return profile?.relationships && !profile.relationships?.is_colleague && !profile.relationships?.is_banned
  }

  canUnbanProfile(profile: ProfileModel): boolean {
    return profile?.relationships && !profile.relationships.is_colleague && profile.relationships?.is_banned
  }

  canAddToPersonalContacts(profile: ProfileModel): boolean {
    return profile?.relationships && !profile.relationships.is_personal_contact
  }

  canRemoveFromPersonalContacts(profile: ProfileModel): boolean {
    return profile?.relationships && profile.relationships.is_personal_contact
  }
}

const profilesStore = new ProfilesStore()

export {
  ProfilesStore,
  profilesStore
}
